// Màn POS: khách đang chọn có thẻ liệu trình còn nợ → nhắc và thu tiền thẻ ngay tại quầy (in phiếu thu sau khi thu)
import { useMemo, useState } from 'react';
import { Modal } from 'antd';
import { AlertTriangle, TicketCheck } from 'lucide-react';
import type { TreatmentCard } from './data';
import { cardFlags, installmentRows } from './logic';
import useTreatmentStore from './store';
import { PayModal } from './modals';
import { pendingRequestOf } from './PayRequestModal';
import { fmtDate, money } from './ui';

export default function PosCardDebt({ customerId }: { customerId: string | null }) {
  const cards = useTreatmentStore(s => s.cards);
  const cfg = useTreatmentStore(s => s.config);
  const requests = useTreatmentStore(s => s.payRequests);
  const [listOpen, setListOpen] = useState(false);
  const [paying, setPaying] = useState<TreatmentCard | null>(null);

  const list = useMemo(() => (customerId ? cards.filter(c => c.customerId === customerId).map(c => ({ c, f: cardFlags(c, cfg) })).filter(x => x.f.debt > 0) : []), [cards, cfg, customerId]);
  const debt = list.reduce((s, x) => s + x.f.debt, 0);
  const overdue = list.reduce((s, x) => s + x.f.overdueAmount, 0);

  const pick = (c: TreatmentCard) => { setListOpen(false); setPaying(c); };

  return (
    <>
      {list.length > 0 && (
        // Một dòng mảnh để nhường chỗ cho giỏ hàng
        <div className={`mt-1.5 flex items-center justify-between gap-2 rounded-md px-2.5 py-1 text-xs ${overdue ? 'bg-red-50 text-red-800' : 'bg-amber-50 text-amber-900'}`}>
          <span className="flex min-w-0 items-center gap-1.5 truncate">
            {overdue ? <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> : <TicketCheck className="h-3.5 w-3.5 shrink-0" />}
            <span className="truncate">{list.length} thẻ còn nợ <b>{money(debt)}đ</b>{overdue ? <> · quá hạn <b>{money(overdue)}đ</b></> : ''}</span>
          </span>
          <button onClick={() => (list.length === 1 ? pick(list[0].c) : setListOpen(true))}
            className="shrink-0 font-semibold text-[#2c6b7f] underline-offset-2 hover:underline">Thu tiền thẻ ›</button>
        </div>
      )}
      <Modal open={listOpen} onCancel={() => setListOpen(false)} footer={null} title="Thẻ liệu trình còn nợ" centered width={520}>
        <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
          {list.map(({ c, f }) => {
            const next = installmentRows(c, cfg).find(r => r.left > 0);
            return (
              <div key={c.id} className="flex items-center justify-between gap-3 px-3 py-2.5 text-sm">
                <div className="min-w-0">
                  <div className="font-semibold">{c.packageName} <span className="font-mono text-xs text-slate-400">{c.id}</span></div>
                  <div className="text-xs text-slate-500">
                    Còn nợ <b className={f.overdue ? 'text-red-600' : 'text-amber-700'}>{money(f.debt)}đ</b>
                    {next && <> · đợt {next.no} hạn {fmtDate(next.dueDate)}{next.state === 'overdue' && <span className="text-red-600"> (trễ {next.overdueDays} ngày)</span>}</>}
                  </div>
                </div>
                <button onClick={() => pick(c)} className="shrink-0 rounded-lg bg-[#357f96] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#2c6b7f]">Thu tiền</button>
              </div>
            );
          })}
        </div>
      </Modal>
      <PayModal card={paying} request={paying ? pendingRequestOf(requests, paying.id) : null} onClose={() => setPaying(null)} />
    </>
  );
}
